import React, { useCallback, useState, lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, ZapIcon, ShieldCheckIcon, LockKeyholeIcon, CreditCardIcon } from 'lucide-react';
interface PurchaseSectionProps {
  id?: string;
}
type PlanType = 'combo' | 'kit';
export const PurchaseSection = memo(function PurchaseSection({
  id
}: PurchaseSectionProps) {
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('combo');
  const handlePurchase = useCallback(() => {
    if (selectedPlan === 'combo') {
      window.location.href = 'https://centershop.store/cart/47834933657845:1';
    } else {
      window.location.href = 'https://centershop.store/cart/47529204318453:1';
    }
  }, [selectedPlan]);
  const plans = {
    combo: {
      name: 'COMBO PRO',
      description: 'Kit de Rendimiento Personal + MENTE POTENCIADA',
      originalPrice: '$49.990',
      price: '$19.990',
      discount: '60% OFF',
      badge: '⭐ MÁS ELEGIDO',
      savings: 'AHORRÁS $30.000',
      items: ['Método práctico principal', 'Bono 1 — Reset Personal', 'Bono 2 — Mente en Calma', 'Bono 3 — Control Total', '✨ MENTE POTENCIADA (producto digital extra)', 'Acceso inmediato y de por vida']
    },
    kit: {
      name: 'KIT DE RENDIMIENTO PERSONAL',
      description: 'Ebook principal + 3 Bonos',
      originalPrice: '$39.990',
      price: '$14.990',
      discount: '62% OFF',
      badge: null,
      savings: null,
      items: ['Método práctico principal', 'Bono 1 — Reset Personal', 'Bono 2 — Mente en Calma', 'Bono 3 — Control Total', 'Acceso inmediato y de por vida']
    }
  };
  return <section id={id} className="w-full py-16 px-4 bg-gradient-to-b from-[#0b1628] to-[#0f2035] text-white" data-id="element-37">
      <div className="max-w-xl mx-auto" data-id="element-38">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="relative" data-id="element-39">
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-40" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-500/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" data-id="element-41" />

          <div className="relative z-10 text-center mb-8" data-id="element-42">
            <h2 className="text-3xl md:text-4xl font-bold mb-3 text-white" data-id="element-43">
              Elegí tu plan y empezá hoy
            </h2>
            <p className="text-slate-400 text-base" data-id="element-44">
              Seleccioná la opción que mejor se adapte a vos
            </p>
          </div>

          {/* Plan selection cards */}
          <div className="relative z-10 space-y-4 mb-8" data-id="element-45">
            {/* COMBO PRO option */}
            <motion.div whileTap={{
            scale: 0.99
          }} onClick={useCallback(() => setSelectedPlan('combo'), [])} className={`relative cursor-pointer rounded-2xl p-5 md:p-6 transition-all duration-300 ${selectedPlan === 'combo' ? 'bg-[#0f2035] border-2 border-amber-500 shadow-lg shadow-amber-500/20' : 'bg-[#0f2035]/60 border border-[#1a3050] opacity-70 hover:opacity-90'}`} data-id="element-46">
              {/* MÁS ELEGIDO badge */}
              <div className="absolute -top-3 left-5" data-id="element-47">
                <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide flex items-center gap-1.5 shadow-md shadow-amber-500/30" data-id="element-48">
                  <ZapIcon className="w-3 h-3" data-id="element-49" />
                  MÁS ELEGIDO
                </span>
              </div>

              <div className="flex items-start gap-4 mt-1" data-id="element-50">
                {/* Radio indicator */}
                <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-1 flex items-center justify-center transition-all ${selectedPlan === 'combo' ? 'border-amber-500 bg-amber-500' : 'border-slate-500'}`} data-id="element-51">
                  {selectedPlan === 'combo' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-52" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0" data-id="element-53">
                  <div className="flex items-start justify-between gap-3" data-id="element-54">
                    <div data-id="element-55">
                      <h3 className="text-lg font-bold text-white leading-tight" data-id="element-56">
                        🏆 COMBO PRO
                      </h3>
                      <p className="text-slate-400 text-xs mt-1" data-id="element-57">
                        Kit +{' '}
                        <span className="text-amber-400 font-semibold" data-id="element-58">
                          MENTE POTENCIADA
                        </span>
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0" data-id="element-59">
                      <p className="text-slate-500 text-sm line-through decoration-red-500" data-id="element-60">
                        {plans.combo.originalPrice}
                      </p>
                      <p className="text-amber-400 text-2xl font-extrabold leading-tight" data-id="element-61">
                        {plans.combo.price}
                      </p>
                    </div>
                  </div>

                  {/* Savings badge */}
                  <div className="mt-3 flex items-center gap-2" data-id="element-62">
                    <span className="bg-emerald-500/20 text-emerald-400 px-3 py-0.5 rounded-full text-[11px] font-bold" data-id="element-63">
                      AHORRÁS $45.000
                    </span>
                    <span className="bg-red-500/20 text-red-400 px-3 py-0.5 rounded-full text-[11px] font-bold" data-id="element-64">
                      64% OFF
                    </span>
                  </div>

                  {/* Items list (shown when selected) */}
                  {selectedPlan === 'combo' && <motion.ul initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} transition={{
                  duration: 0.3
                }} className="mt-4 space-y-2" data-id="element-65">
                      {plans.combo.items.map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-66">
                          <CheckCircle2Icon className={`w-3.5 h-3.5 flex-shrink-0 ${item.includes('MENTE POTENCIADA') ? 'text-amber-400' : 'text-teal-400'}`} data-id="element-67" />
                          <span className={`text-xs ${item.includes('MENTE POTENCIADA') ? 'text-amber-300 font-semibold' : 'text-slate-300'}`} data-id="element-68">
                            {item}
                          </span>
                        </li>)}
                    </motion.ul>}
                </div>
              </div>
            </motion.div>

            {/* KIT solo option */}
            <motion.div whileTap={{
            scale: 0.99
          }} onClick={useCallback(() => setSelectedPlan('kit'), [])} className={`relative cursor-pointer rounded-2xl p-5 md:p-6 transition-all duration-300 ${selectedPlan === 'kit' ? 'bg-[#0f2035] border-2 border-teal-500 shadow-lg shadow-teal-500/20' : 'bg-[#0f2035]/60 border border-[#1a3050] opacity-70 hover:opacity-90'}`} data-id="element-69">
              <div className="flex items-start gap-4" data-id="element-70">
                {/* Radio indicator */}
                <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 mt-1 flex items-center justify-center transition-all ${selectedPlan === 'kit' ? 'border-teal-500 bg-teal-500' : 'border-slate-500'}`} data-id="element-71">
                  {selectedPlan === 'kit' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-72" />}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0" data-id="element-73">
                  <div className="flex items-start justify-between gap-3" data-id="element-74">
                    <div data-id="element-75">
                      <h3 className="text-lg font-bold text-white leading-tight" data-id="element-76">
                        📘 KIT DE RENDIMIENTO
                      </h3>
                      <p className="text-slate-400 text-xs mt-1" data-id="element-77">
                        Método práctico principal + 3 Bonos
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0" data-id="element-78">
                      <p className="text-slate-500 text-sm line-through decoration-red-500" data-id="element-79">
                        {plans.kit.originalPrice}
                      </p>
                      <p className="text-amber-400 text-2xl font-extrabold leading-tight" data-id="element-80">
                        {plans.kit.price}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3" data-id="element-81">
                    <span className="bg-teal-500/20 text-teal-300 px-3 py-0.5 rounded-full text-[11px] font-bold" data-id="element-82">
                      62% OFF
                    </span>
                  </div>

                  {/* Items list (shown when selected) */}
                  {selectedPlan === 'kit' && <motion.ul initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} transition={{
                  duration: 0.3
                }} className="mt-4 space-y-2" data-id="element-83">
                      {plans.kit.items.map((item, i) => <li key={i} className="flex items-center gap-2" data-id="element-84">
                          <CheckCircle2Icon className="w-3.5 h-3.5 text-teal-400 flex-shrink-0" data-id="element-85" />
                          <span className="text-xs text-slate-300" data-id="element-86">{item}</span>
                        </li>)}
                    </motion.ul>}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Conversion push message */}
          <motion.div initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="relative z-10 text-center mb-6" data-id="element-87">
            <p className="text-xl md:text-2xl font-extrabold text-white leading-snug" data-id="element-88">
              Estás a un paso de cambiar esto...
            </p>
            <div className="w-12 h-0.5 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto mt-3 rounded-full" data-id="element-89" />
          </motion.div>

          {/* Single CTA Button */}
          <div className="relative z-10" data-id="element-90">
            <motion.button whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.97
          }} onClick={handlePurchase} className="w-full py-5 bg-gradient-to-r from-amber-500 to-orange-600 text-white text-xl font-bold rounded-xl shadow-lg shadow-orange-500/30 flex items-center justify-center gap-3 hover:brightness-110 transition-all" data-id="element-91">
              {selectedPlan === 'combo' ? 'SÍ, QUIERO EL COMBO COMPLETO — $19.990' : 'QUIERO SALIR DEL CAOS — $14.990'}
            </motion.button>

            {/* Guarantee block */}
            <div className="mt-6 bg-[#0f2035] border border-emerald-500/30 rounded-xl p-6" data-id="element-92">
              <div className="flex items-center gap-3 mb-3" data-id="element-93">
                <ShieldCheckIcon className="w-6 h-6 text-emerald-400 flex-shrink-0" data-id="element-94" />
                <h4 className="text-emerald-300 font-bold text-lg" data-id="element-95">
                  🛡️ Garantía de 30 días — Riesgo cero para vos
                </h4>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed" data-id="element-96">
                Abrís el kit. Revisás los bonos. Empezás a aplicar el sistema.
                <br data-id="element-97" />
                <br data-id="element-98" />
                Si en los próximos 30 días sentís que no te aportó claridad ni
                dirección para crecer... te devolvemos el 100% de tu dinero.
                <br data-id="element-99" />
                <br data-id="element-100" />
                Sin formularios complicados. Sin preguntas incómodas. Sin
                vueltas.
                <br data-id="element-101" />
                <br data-id="element-102" />
                La confianza que tenemos en el Método C.S.M. nos permite hacer
                esa promesa sin dudar.
                <br data-id="element-103" />
                <br data-id="element-104" />
                El riesgo es completamente nuestro. El beneficio es
                completamente tuyo.
              </p>
            </div>

            {/* Secure Payment block */}
            <div className="mt-5 bg-[#0f2035] border border-[#1a3050] rounded-xl p-5" data-id="element-105">
              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-106">
                <LockKeyholeIcon className="w-5 h-5 text-sky-400" data-id="element-107" />
                <h4 className="text-white font-bold text-base" data-id="element-108">
                  Pago 100% seguro con Mercado Pago
                </h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-109">
                <div className="flex items-center gap-2.5 bg-[#0b1628]/60 rounded-lg px-3.5 py-3" data-id="element-110">
                  <CreditCardIcon className="w-4.5 h-4.5 text-sky-400 flex-shrink-0" data-id="element-111" />
                  <span className="text-slate-300 text-xs leading-tight" data-id="element-112">
                    Aceptamos tarjeta de crédito y débito
                  </span>
                </div>
                <div className="flex items-center gap-2.5 bg-[#0b1628]/60 rounded-lg px-3.5 py-3" data-id="element-113">
                  <ShieldCheckIcon className="w-4.5 h-4.5 text-emerald-400 flex-shrink-0" data-id="element-114" />
                  <span className="text-slate-300 text-xs leading-tight" data-id="element-115">
                    Tus datos están protegidos
                  </span>
                </div>
                <div className="flex items-center gap-2.5 bg-[#0b1628]/60 rounded-lg px-3.5 py-3" data-id="element-116">
                  <ZapIcon className="w-4.5 h-4.5 text-amber-400 flex-shrink-0" data-id="element-117" />
                  <span className="text-slate-300 text-xs leading-tight" data-id="element-118">
                    Acceso inmediato al completar la compra
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-center mt-4" data-id="element-119">
                <img src="https://cdn.magicpatterns.com/uploads/9cxWACBMmqzvQJA1DEdhom/MP_RGB_HANDSHAKE_pluma_vertical.png" alt="Mercado Pago" loading="lazy" className="h-12 object-contain" data-id="element-120" />
              </div>
            </div>

            <p className="mt-4 text-slate-500 text-xs text-center" data-id="element-121">
              Pago único. Acceso seguro. Sin suscripciones.
            </p>
          </div>
        </motion.div>
      </div>
    </section>;
});